import React, { useEffect, useState } from "react";

import {
    useNavigate,
    useParams
} from "react-router-dom";

import {
    getGRNById
} from "../services/api";

import {
    Printer,
    ArrowLeft,
    RefreshCw
} from "lucide-react";

import "./GRNPrint.css";


const COMPANY = {

    name:
        "Bhimashankar Agro Coldchain & Processing Producer Co. Ltd.",

    address:
        "Add. Gat No. 628, A/p- Pargaon (Peth), Tal. Ambegaon, Dist. Pune - 410 512",

    mobile:
        "Mob. 9822974495 / 9921366271 / 9921252022",

    email:
        "bhimashankaragrocoldchain@gmail.com"

};


const GRNPrint = () => {

    const {
        id
    } = useParams();

    const navigate =
        useNavigate();


    const [
        grn,
        setGrn
    ] = useState(null);


    const [
        loading,
        setLoading
    ] = useState(true);


    const [
        error,
        setError
    ] = useState("");


    // =====================================================
    // LOAD GRN
    // =====================================================

    const loadGRN = async () => {

        try {

            setLoading(true);

            setError("");

            const response =
                await getGRNById(id);

            console.log(
                "GRN Print Response:",
                response
            );


            const data =
                response?.data?.data ||
                response?.data;


            if (!data) {

                throw new Error(
                    "GRN data not found."
                );

            }


            setGrn(data);

        } catch (err) {

            console.error(
                "GRN Print Error:",
                err
            );

            setError(
                err?.response?.data?.message ||
                err?.message ||
                "Unable to load GRN."
            );

        } finally {

            setLoading(false);

        }

    };


    // =====================================================
    // INITIAL LOAD
    // =====================================================

    useEffect(() => {

        loadGRN();

    }, [id]);


    // =====================================================
    // FORMAT DATE
    // =====================================================

    const formatDate = (
        value
    ) => {

        if (!value) {

            return "-";

        }


        const date =
            new Date(value);


        if (
            Number.isNaN(
                date.getTime()
            )
        ) {

            return String(value)
                .substring(0, 10);

        }


        return date.toLocaleDateString(
            "en-IN",
            {
                day: "2-digit",
                month: "2-digit",
                year: "numeric"
            }
        );

    };


    // =====================================================
    // NUMBER FORMAT
    // =====================================================

    const number = (
        value
    ) => {

        const numericValue =
            Number(value || 0);


        return numericValue.toLocaleString(
            "en-IN",
            {
                maximumFractionDigits: 2
            }
        );

    };


    // =====================================================
    // PRINT
    // =====================================================

    const printDocument = () => {

        window.print();

    };


    // =====================================================
    // LOADING
    // =====================================================

    if (loading) {

        return (

            <div className="grn-print-loading">

                <RefreshCw
                    size={24}
                    className="grn-loading-icon"
                />

                <span>
                    Loading GRN...
                </span>

            </div>

        );

    }


    // =====================================================
    // ERROR
    // =====================================================

    if (
        error ||
        !grn
    ) {

        return (

            <div className="grn-print-error">

                <h2>
                    GRN Not Found
                </h2>

                <p>
                    {
                        error ||
                        "The requested GRN could not be found."
                    }
                </p>

                <button
                    type="button"
                    onClick={() =>
                        navigate(
                            "/goods-received"
                        )
                    }
                >

                    <ArrowLeft
                        size={16}
                    />

                    Back

                </button>

            </div>

        );

    }


    // =====================================================
    // BASIC DATA
    // =====================================================

    const grnNo =
        grn.grnNo ||
        grn.grn_no ||
        "-";


    const grnDate =
        grn.grnDate ||
        grn.grn_date ||
        null;


    const customerName =
        grn.customerName ||
        grn.customer_name ||
        "-";


    const vehicleNo =
        grn.vehicleNo ||
        grn.vehicle_no ||
        "-";


    const driverName =
        grn.driverName ||
        grn.driver_name ||
        "-";


    // IMPORTANT:
    // Party No. supports both frontend and DB naming.

    const partyNo =
        grn.partyNo ||
        grn.party_no ||
        "-";


    const fullLorryWeight =
        grn.fullLorryWeightKg ??
        grn.full_lorry_weight_kg ??
        0;


    const emptyLorryWeight =
        grn.emptyLorryWeightKg ??
        grn.empty_lorry_weight_kg ??
        0;


    const netWeight =
        grn.netWeightKg ??
        grn.net_weight_kg ??
        (
            Number(fullLorryWeight) -
            Number(emptyLorryWeight)
        );


    const remarks =
        grn.remarks ||
        "";


    const items =
        Array.isArray(grn.items)
            ? grn.items
            : [];


    // =====================================================
    // TOTAL BAGS
    // =====================================================

    const totalBags =
        items.reduce(
            (
                total,
                item
            ) => {

                return (
                    total +
                    Number(
                        item.bagsQuantity ??
                        item.bags_quantity ??
                        item.bags ??
                        0
                    )
                );

            },
            0
        );


    const totalRottenBags =
        items.reduce(
            (total, item) =>
                total + Number(
                    item.rotten_bags ??
                    item.rottenBags ??
                    0
                ),
            0
        );

    const totalUsableBags =
        totalBags - totalRottenBags;


    // =====================================================
    // TOTAL WEIGHT
    // =====================================================

    const totalWeight =
        items.reduce(
            (
                total,
                item
            ) => {

                return (
                    total +
                    Number(
                        item.weightKg ??
                        item.weight_kg ??
                        item.weight ??
                        0
                    )
                );

            },
            0
        );


    // =====================================================
    // PAGE
    // =====================================================

    return (

        <div className="grn-print-page">


            {/* =================================================
                ACTION BAR
            ================================================= */}

            <div className="grn-print-actions no-print">

                <button
                    type="button"
                    className="grn-back-button"
                    onClick={() =>
                        navigate(
                            "/goods-received"
                        )
                    }
                >

                    <ArrowLeft
                        size={17}
                    />

                    Back

                </button>


                <div className="grn-action-right">

                    <button
                        type="button"
                        className="grn-refresh-button"
                        onClick={
                            loadGRN
                        }
                    >

                        <RefreshCw
                            size={16}
                        />

                        Refresh

                    </button>


                    <button
                        type="button"
                        className="grn-print-button"
                        onClick={
                            printDocument
                        }
                    >

                        <Printer
                            size={17}
                        />

                        Print / Save PDF

                    </button>

                </div>

            </div>


            {/* =================================================
                A4 DOCUMENT
            ================================================= */}

            <div className="grn-document">


                {/* =================================================
                    COMPANY HEADER
                ================================================= */}

                <div className="grn-company-header">

                    <h1>
                        {COMPANY.name}
                    </h1>

                    <p>
                        {COMPANY.address}
                    </p>

                    <p>
                        {COMPANY.mobile}
                    </p>

                    <p>
                        {COMPANY.email}
                    </p>

                </div>


                {/* =================================================
                    DOCUMENT TITLE
                ================================================= */}

                <div className="grn-title-row">

                    <div className="grn-title">

                        GOOD RECEIVED NOTE

                        <span>
                            (आवक)
                        </span>

                    </div>

                </div>


                {/* =================================================
                    BASIC DETAILS
                ================================================= */}

                <div className="grn-basic-details">


                    {/* GRN NUMBER + DATE */}

                    <div className="grn-detail-row">

                        <div className="grn-detail-label">
                            No:
                        </div>

                        <div className="grn-detail-value">
                            {grnNo}
                        </div>


                        <div className="grn-date-label">
                            Date:
                        </div>

                        <div className="grn-date-value">
                            {formatDate(
                                grnDate
                            )}
                        </div>

                    </div>


                    {/* CUSTOMER */}

                    <div className="grn-detail-row">

                        <div className="grn-detail-label">
                            M/s.
                        </div>

                        <div className="grn-wide-value">
                            {customerName}
                        </div>

                    </div>


                    {/* VEHICLE */}

                    <div className="grn-detail-row">

                        <div className="grn-detail-label">
                            Vehicle No.
                        </div>

                        <div className="grn-wide-value">
                            {vehicleNo}
                        </div>

                    </div>


                    {/* DRIVER */}

                    <div className="grn-detail-row">

                        <div className="grn-detail-label">
                            Driver
                        </div>

                        <div className="grn-wide-value">
                            {driverName}
                        </div>

                    </div>


                    {/* PARTY NUMBER */}

                    <div className="grn-detail-row">

                        <div className="grn-detail-label">
                            Party No.
                        </div>

                        <div className="grn-wide-value">
                            {partyNo}
                        </div>

                    </div>

                </div>


                {/* =================================================
                    DESCRIPTION
                ================================================= */}

                <div className="grn-description">

                    Received and store following goods
                    on the terms &amp; conditions as
                    stipulated overleaf.

                </div>


                {/* =================================================
                    ITEMS TABLE
                ================================================= */}

                <div className="grn-items-scroll">
                   <table className="grn-items-table">


                    {/* =================================================
                        TABLE HEADER
                    ================================================= */}

                    <thead>

                        <tr>

                            <th className="lot-column">
                                Lot No.
                            </th>

                            <th className="chamber-column">
                                Chamber No.
                            </th>

                            <th className="item-column">
                                Item Name
                            </th>

                            <th className="mark-column">
                                Product Mark
                            </th>

                            <th className="qty-column">
                                Received Bags
                            </th>

                            <th className="qty-column">
                                Rotten Bags
                            </th>

                            <th className="qty-column">
                                Usable Bags
                            </th>

                            <th className="weight-column">
                                Weight
                            </th>

                            <th className="location-column">
                                Location
                            </th>

                        </tr>

                    </thead>


                    {/* =================================================
                        TABLE BODY
                    ================================================= */}

                    <tbody>

                        {items.map(
                            (
                                item,
                                index
                            ) => {


                                // -----------------------------
                                // LOT
                                // -----------------------------

                                const lotNo =
                                    item.lotNo ||
                                    item.lot_no ||
                                    "-";


                                // -----------------------------
                                // CHAMBER
                                // -----------------------------

                                const rawChamberNo =
                                    item.chamberNo ||
                                    item.chamber_no ||
                                    item.chamberNumber ||
                                    item.chamber_number ||
                                    "-";

                                const chamberLabels = {
                                    "C-01": "A",
                                    "C-02": "B",
                                    "C-03": "C",
                                    "C-04": "D"
                                };

                                const baseChamberNo =
                                    chamberLabels[String(rawChamberNo).trim().toUpperCase()] ||
                                    rawChamberNo;

                                const floorNo = item.floor_no ?? item.floorNo;

                                const chamberNo =
                                    floorNo !== null &&
                                    floorNo !== undefined &&
                                    String(floorNo).trim() !== ""
                                        ? `${baseChamberNo}-${floorNo}`
                                        : baseChamberNo;


                                // -----------------------------
                                // ITEM NAME
                                // -----------------------------

                                const itemName =
                                    item.itemName ||
                                    item.item_name ||
                                    item.description ||
                                    "Potato";


                                // -----------------------------
                                // PRODUCT MARK
                                // -----------------------------

                                const productMark =
                                    item.productMark ||
                                    item.product_mark ||
                                    "-";


                                // -----------------------------
                                // BAGS
                                // -----------------------------

                                const bags =
                                    item.bagsQuantity ??
                                    item.bags_quantity ??
                                    item.bags ??
                                    0;

                                const rottenBags = Number(
                                    item.rotten_bags ??
                                    item.rottenBags ??
                                    0
                                );

                                const usableBags = Math.max(
                                    0,
                                    Number(bags) - rottenBags
                                );


                                // -----------------------------
                                // WEIGHT
                                // -----------------------------

                                const weight =
                                    item.weightKg ??
                                    item.weight_kg ??
                                    item.weight ??
                                    0;


                                // -----------------------------
                                // LOCATION
                                // -----------------------------

                                const location =
                                    item.location ||
                                    "-";


                                return (

                                    <tr
                                        key={index}
                                    >


                                        {/* LOT NO. */}

                                        <td className="lot-column">

                                            {lotNo}

                                        </td>


                                        {/* CHAMBER NO. */}

                                        <td className="chamber-column">

                                            {chamberNo}

                                        </td>


                                        {/* ITEM NAME */}

                                        <td className="item-column">

                                            {itemName}

                                        </td>


                                        {/* PRODUCT MARK */}

                                        <td className="mark-column">

                                            {productMark}

                                        </td>


                                        {/* RECEIVED BAGS */}

                                        <td className="qty-column center">
                                            {number(bags)}
                                        </td>

                                        {/* ROTTEN BAGS */}

                                        <td className="qty-column center">
                                            {number(rottenBags)}
                                        </td>

                                        {/* USABLE BAGS */}

                                        <td className="qty-column center">
                                            {number(usableBags)}
                                        </td>


                                        {/* WEIGHT */}

                                        <td
                                            className="weight-column right"
                                        >

                                            {number(
                                                weight
                                            )}

                                        </td>


                                        {/* LOCATION */}

                                        <td className="location-column">

                                            {location}

                                        </td>

                                    </tr>

                                );

                            }
                        )}


                        {/* =================================================
                            EMPTY ROWS
                        ================================================= */}

                        {Array.from({

                            length:
                                Math.max(
                                    0,
                                    6 - items.length
                                )

                        }).map(
                            (
                                _,
                                index
                            ) => (

                                <tr
                                    key={
                                        `empty-${index}`
                                    }
                                    className="empty-print-row"
                                >

                                    <td></td>

                                    <td></td>

                                    <td></td>

                                    <td></td>

                                    <td></td>

                                    <td></td>

                                    <td></td>

                                    <td></td>

                                    <td></td>

                                </tr>

                            )
                        )}

                    </tbody>


                    {/* =================================================
                        TOTAL
                    ================================================= */}

                    <tfoot>

                        <tr>

                            <td
                                colSpan="4"
                                className="total-label"
                            >

                                Total

                            </td>


                            <td className="qty-column center">
                                {number(totalBags)}
                            </td>

                            <td className="qty-column center">
                                {number(totalRottenBags)}
                            </td>

                            <td className="qty-column center">
                                {number(totalUsableBags)}
                            </td>


                            


                            <td
                                className="weight-column right"
                            >

                                {number(
                                    totalWeight
                                )}

                            </td>


                            <td
                                className="location-column"
                            ></td>

                        </tr>

                    </tfoot>

               </table>
                    </div>

                {/* =================================================
                    REMARKS
                ================================================= */}

                <div className="grn-remarks">

                    <div className="grn-remarks-label">

                        Remarks

                    </div>

                    <div className="grn-remarks-value">

                        {remarks || " "}

                    </div>

                </div>


                {/* =================================================
                    WEIGHT DETAILS
                ================================================= */}

                <div className="grn-weight-section">


                   <div>

                        <span>
                            Full Lorry Weight
                        </span>

                        <strong>

                            {number(
                                fullLorryWeight
                            )}

                            {" KG"}

                        </strong>

                    </div>


                    <div>

                        <span>
                            Empty Lorry Weight
                        </span>

                        <strong>

                            {number(
                                emptyLorryWeight
                            )}

                            {" KG"}

                        </strong>

                    </div>


                    <div>

                        <span>
                            Net Weight
                        </span>

                        <strong>

                            {number(
                                netWeight
                            )}

                            {" KG"}

                        </strong>

                    </div>

                </div>


                {/* =================================================
                    FOOTER
                ================================================= */}

                <div className="grn-footer-text">

                    <div>

                        We confirm the above &amp; agree
                        to store the goods on the terms
                        &amp; conditions as stipulated
                        overleaf.

                    </div>


                    <div className="grn-footer-company">

                        For {COMPANY.name}

                    </div>

                </div>


                {/* =================================================
                    SIGNATURES
                ================================================= */}

                <div className="grn-signatures">

                    <div>
                        <span>
                            Customer's Rep.
                        </span>
                    </div>


                    <div>
                        <span>
                            ENT Fno.
                        </span>
                    </div>


                    <div>
                        <span>
                            CKD by
                        </span>
                    </div>


                    <div>
                        <span>
                            Auditor
                        </span>
                    </div>


                    <div>
                        <span>
                            Store Incharge
                        </span>
                    </div>

                </div>

            </div>

        </div>

    );

};


export default GRNPrint;
